import { CloseIcon } from "@src/components/elements/assets";
import { DelegateIcon } from "@src/components/elements/assets/DelegateIcon";
import React, { PropsWithChildren, forwardRef } from "react";
import styled from "styled-components/macro";

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  id?: string;
  onRemove?: (id?: number | string) => void;
  disabled?: boolean;
  backgroundColor?: string;
  fontColor?: string;
  className?: string;
  size?: "small" | "medium";
}

export const ChipStyledRoot = styled.div<{
  backgroundColor?: string;
  fontColor?: string;
  disabled: boolean;
  size?: "small" | "medium";
}>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  gap: ${({ size }) => (size === "small" ? "0.25rem" : "0.5rem")};

  font-size: var(--font-size-xxsmall);
  background-color: ${({ disabled, backgroundColor }) =>
    disabled
      ? "var(--background-gray)"
      : backgroundColor ?? "var(--border-gray-light)"};
  color: ${({ fontColor, disabled }) =>
    fontColor ? fontColor : disabled ? "var(--gray)" : "var(--dark-gray)"};
  border: 1px solid
    ${({ disabled }) => (disabled ? "var(--border-gray-light)" : "transparent")};
  padding: ${({ size }) =>
    size === "small" ? "0.15rem 0.25rem" : "0.25rem 0.5rem"};
  line-height: ${({ size }) => (size === "small" ? "1em" : "1rem")};
  border-radius: 999px;
  white-space: nowrap;
  font-family: var(--inter-font);
  font-weight: ${({ size }) =>
    size === "small"
      ? "var(--font-weight-regular);"
      : "var(--font-weight-bold);"};
  width: fit-content;
  max-width: 100%;
  overflow: hidden;

  .close {
    height: 0.75rem;
    width: auto;
  }

  .chip-icon {
    width: 1em;
    height: 1em;
  }
  &.approved {
    background: var(--background-green);
    color: var(--dark-green);
    svg {
      fill: var(--green);
    }
  }

  &.bypassed {
    background: var(--background-blue);
    color: var(--dark-blue);
  }

  &.processing {
    background: var(--light-blue);
    color: var(--blue);
    svg {
      fill: var(--blue);
    }
  }

  &.pending {
    background: var(--N800);
    color: var(--white);
  }

  &.rejected {
    fill: var(--error-color);
    color: var(--error-color);
    background: var(--background-error-light);
  }
`;

export const Chip = forwardRef<HTMLDivElement, PropsWithChildren<Props>>(
  (
    {
      onRemove,
      id,
      children,
      disabled,
      backgroundColor,
      fontColor,
      className,
      ...props
    },
    ref,
  ) => {
    return (
      <ChipStyledRoot
        className={className}
        disabled={disabled ?? false}
        backgroundColor={backgroundColor}
        fontColor={fontColor}
        ref={ref}
        {...props}
      >
        {children}
        {onRemove ? (
          <CloseIcon className="close" onClick={() => onRemove(id)} />
        ) : null}
      </ChipStyledRoot>
    );
  },
);

Chip.displayName = "Chip";

export const DelegateChip = forwardRef<
  HTMLDivElement,
  PropsWithChildren<Props>
>((props, ref) => {
  return (
    <Chip
      {...props}
      backgroundColor="var(--alert-color-background)"
      fontColor="var(--alert-color)"
      ref={ref}
    >
      <DelegateIcon fill="var(--alert-color)" className="chip-icon" /> Delegate
    </Chip>
  );
});

DelegateChip.displayName = "DelegateChip";
